import { useState } from 'react';
import { useAgentMutations, useAgents } from '../lib/queries';
import type { Agent, AgentApproval } from '../data/types';
import { Chip, Empty, Loading, Pill, Tile, statusTone } from '../components/ui';
import { AgentCounter } from './AgentCounter';
import { errorText, loadFailedHint } from '../lib/errors';

const REASONS = [
  'Incomplete documents', 'Failed KYC check', 'Area already covered', 'Repeated cash mismatches',
];

const when = (iso: string | null | undefined) =>
  (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

/** Commission as the operator set it, or the network default if they did not. */
function commission(a: Agent): string {
  if (a.commissionMode === 'fixed' && a.commissionFixed != null) {
    return `${(Number(a.commissionFixed) / 100).toFixed(2)} flat`;
  }
  if (a.commissionMode === 'percent' && a.commissionPct != null) return `${a.commissionPct}%`;
  return '—';
}

export function Agents() {
  const { data, isLoading, error } = useAgents();
  const { approve, decline, create } = useAgentMutations();
  const [filter, setFilter] = useState<'all' | 'appealed' | AgentApproval>('all');
  const [declining, setDeclining] = useState<Agent | null>(null);
  const [reason, setReason] = useState(REASONS[0]!);
  const [adding, setAdding] = useState(false);
  const [counterOf, setCounterOf] = useState<string | null>(null);
  /* Approve has no dialog, so its refusal goes above the list; decline and
     register say theirs inside the dialog that is still open. All three used
     to fail without a word. */
  const [pageError, setPageError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  if (isLoading) return <Loading />;
  if (error || !data) {
    return <Empty title="Agent records are not available." hint={loadFailedHint(error)} />;
  }

  /* An appealed counter is still 'declined', so filtering on approval alone
     hid it under Declined — which is not where anyone looks for something that
     needs deciding. An appeal is its own queue. */
  const appealing = (a: Agent) => !!a.appealedAt && !a.appealAnsweredAt;

  const counts = {
    all: data.length,
    pending: data.filter((a) => a.approval === 'pending').length,
    appealed: data.filter(appealing).length,
    approved: data.filter((a) => a.approval === 'approved').length,
    declined: data.filter((a) => a.approval === 'declined').length,
  };
  const shown = data.filter((a) =>
    filter === 'all' ? true : filter === 'appealed' ? appealing(a) : a.approval === filter);

  return (
    <>
      <div className="tiles">
        <Tile k="Agents" v={counts.all} d="cash points" />
        <Tile k="Pending review" v={counts.pending} d={counts.pending ? 'awaiting a decision' : 'queue clear'} />
        <Tile k="Appeals" v={counts.appealed}
              d={counts.appealed ? 'declined, asking again' : 'none open'} />
        <Tile k="Approved" v={counts.approved} d="can take cash" />
        <Tile k="Declined" v={counts.declined} d="cannot take cash" />
      </div>

      <div className="toolbar">
        <div className="chips">
          {(['all', 'pending', 'appealed', 'approved', 'declined'] as const).map((f) => (
            <Chip
              key={f}
              selected={filter === f}
              onClick={() => setFilter(f)}
              label={<>{f === 'all' ? 'All' : f[0]!.toUpperCase() + f.slice(1)} <span className="mono">{counts[f]}</span></>}
            />
          ))}
        </div>
        <button className="btn sm" onClick={() => { setDialogError(null); setAdding(true); }}>Register agent</button>
      </div>

      {pageError && (
        <div className="note" role="alert" style={{ marginTop: 12, color: 'var(--bad)' }}>{pageError}</div>
      )}

      {!shown.length ? (
        <Empty
          title={data.length ? 'No agents in this list.' : 'No agents registered yet.'}
          hint={data.length ? undefined
            : 'Register the first one above. Agents land pending — nobody self-approves.'}
        />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Agent</th><th>Area</th><th>Approval</th>
                <th>Availability</th><th>Spark Agent</th><th className="num">Commission</th><th />
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr key={a.id} className={a.approval === 'declined' ? 'off' : undefined}>
                  <td className="li-title">{a.name}</td>
                  <td>
                    {a.area}
                    <div className="li-sub">
                      {a.selfRegistered ? 'applied from the app' : 'registered'}
                      {' '}{when(a.appliedAt)}
                      {a.selfRegistered && a.emailVerified === false && ' · email unconfirmed'}
                    </div>
                  </td>
                  <td>
                    {/* Closed by the counter itself: declined so every check
                        stops it, but a different fact from an operator's
                        decision, so it says so. */}
                    {a.closedAt
                      ? <Pill tone="off">Deleted {when(a.closedAt)}</Pill>
                      : (
                        <Pill tone={statusTone(a.approval)}>
                          {a.approval[0]!.toUpperCase() + a.approval.slice(1)}
                        </Pill>
                      )}
                    {a.declineReason && <div className="li-sub">{a.declineReason}</div>}
                    {a.decidedBy && <div className="li-sub faint">by {a.decidedBy}</div>}
                    {/* A declined counter asking to be reconsidered. Shown here
                        rather than behind a click: it is the thing that needs
                        deciding, and it sorts into the pending queue for the
                        same reason. */}
                    {a.appealedAt && !a.appealAnsweredAt && (
                      <div className="note" style={{ marginTop: 6 }}>
                        <b>Appealed {when(a.appealedAt)}.</b>
                        {a.appealNote && <div className="li-sub">{a.appealNote}</div>}
                      </div>
                    )}
                  </td>
                  <td>
                    {a.approval === 'approved'
                      ? <Pill tone={statusTone(a.availability)}>{a.availability}</Pill>
                      : <span className="faint">—</span>}
                  </td>
                  <td>
                    {a.hasLogin
                      ? <>
                          <Pill tone={a.onShift ? 'on' : 'off'}>{a.onShift ? 'On shift' : 'Off shift'}</Pill>
                          {a.mustChangePassword && <div className="li-sub">first sign-in pending</div>}
                        </>
                      : <span className="faint">no login</span>}
                    {a.counterCode && <div className="li-sub mono">{a.counterCode}</div>}
                    {!a.closedAt && a.hasLogin && (
                      <div className="li-sub">
                        {a.termsVersion != null
                          ? `Counter Terms v${a.termsVersion}${a.termsAcceptedAt ? ` · ${when(a.termsAcceptedAt)}` : ''}`
                          : 'Counter Terms not agreed yet'}
                      </div>
                    )}
                  </td>
                  <td className="num mono">{commission(a)}</td>
                  <td className="act">
                    {a.approval === 'approved' && (
                      <button className="btn sm ghost" onClick={() => setCounterOf(a.id)}>Counter</button>
                    )}
                    {a.approval !== 'approved' && (
                      <button className="btn sm" disabled={approve.isPending}
                              onClick={() => {
                                setPageError(null);
                                approve.mutate(a.id, {
                                  onError: (e: Error) => setPageError(`${a.name}: ${errorText(e)}`),
                                });
                              }}>
                        Approve
                      </button>
                    )}
                    {a.approval !== 'declined' && (
                      <button className="btn sm ghost" onClick={() => { setDialogError(null); setDeclining(a); setReason(REASONS[0]!); }}>
                        {a.approval === 'approved' ? 'Revoke' : 'Decline'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {declining && (
        <Modal label="Decline agent" onClose={() => setDeclining(null)}>
          <div className="li-title" style={{ fontSize: 15 }}>
            {declining.approval === 'approved' ? 'Revoke' : 'Decline'} {declining.name}
          </div>
          <div className="li-sub" style={{ marginBottom: 10 }}>
            Top-ups this agent already confirmed are not reversed — those are ledger
            entries, and a driver who paid cash yesterday keeps that balance.
          </div>
          <div className="chips" style={{ marginBottom: 12 }}>
            {REASONS.map((r) => (
              <Chip key={r} label={r} selected={reason === r} onClick={() => setReason(r)} />
            ))}
          </div>
          {dialogError && (
            <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginBottom: 10 }}>
              {dialogError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              style={{ flex: 1 }}
              disabled={decline.isPending}
              onClick={() => { setDialogError(null); decline.mutate({ id: declining.id, reason }, {
                onSuccess: () => setDeclining(null),
                onError: (e: Error) => setDialogError(errorText(e)),
              }); }}
            >
              Confirm
            </button>
            <button className="btn ghost" style={{ flex: 1 }} onClick={() => setDeclining(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {counterOf && data.find((a) => a.id === counterOf) && (
        <AgentCounter agent={data.find((a) => a.id === counterOf)!} onClose={() => setCounterOf(null)} />
      )}

      {adding && (
        <AddAgent
          pending={create.isPending}
          error={dialogError}
          onClose={() => setAdding(false)}
          onSubmit={(v) => { setDialogError(null); create.mutate(v, {
            onSuccess: () => setAdding(false),
            onError: (e: Error) => setDialogError(errorText(e)),
          }); }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------ registration */

function AddAgent({ pending, error, onClose, onSubmit }: {
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (v: {
    name: string; area: string;
    commissionMode?: 'percent' | 'fixed'; commissionPct?: number; commissionFixed?: number;
  }) => void;
}) {
  const [name, setName] = useState('');
  const [area, setArea] = useState('');
  const [mode, setMode] = useState<'default' | 'percent' | 'fixed'>('default');
  const [pct, setPct] = useState('2.5');
  const [fixed, setFixed] = useState('20');

  const bad =
    name.trim().length < 2 ? 'Name is too short.'
      : !area.trim() ? 'Area is required — it is how a driver finds them.'
        : mode === 'percent' && !(Number(pct) >= 0 && Number(pct) <= 100) ? 'Percentage must be 0–100.'
          : mode === 'fixed' && !(Number(fixed) >= 0) ? 'Fee cannot be negative.'
            : null;

  return (
    <Modal label="Register agent" onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>Register a cash agent</div>
      <div className="li-sub" style={{ marginBottom: 10 }}>
        They land pending. Approving is a separate decision, made by someone who
        has seen the documents.
      </div>

      <label className="li-sub" htmlFor="ag-name">Name</label>
      <input id="ag-name" className="field" value={name} placeholder="Bole Cash Point"
             onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8 }} />

      <label className="li-sub" htmlFor="ag-area">Area</label>
      <input id="ag-area" className="field" value={area} placeholder="Bole · near Edna Mall"
             onChange={(e) => setArea(e.target.value)} style={{ marginBottom: 10 }} />

      <div className="li-sub">Commission</div>
      <div className="chips" style={{ margin: '4px 0 8px' }}>
        <Chip label="Network default" selected={mode === 'default'} onClick={() => setMode('default')} />
        <Chip label="Percentage" selected={mode === 'percent'} onClick={() => setMode('percent')} />
        <Chip label="Flat fee" selected={mode === 'fixed'} onClick={() => setMode('fixed')} />
      </div>
      {mode === 'percent' && (
        <input className="field mono" value={pct} inputMode="decimal"
               onChange={(e) => setPct(e.target.value)} aria-label="Percentage per top-up" />
      )}
      {mode === 'fixed' && (
        <input className="field mono" value={fixed} inputMode="decimal"
               onChange={(e) => setFixed(e.target.value)} aria-label="Flat fee per top-up" />
      )}

      {(bad || error) && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
          {bad ?? error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
          onClick={() => onSubmit({
            name: name.trim(),
            area: area.trim(),
            ...(mode === 'percent' ? { commissionMode: 'percent' as const, commissionPct: Number(pct) } : {}),
            // Stored in minor units, so a fee is never a float in the database.
            ...(mode === 'fixed' ? { commissionMode: 'fixed' as const, commissionFixed: Math.round(Number(fixed) * 100) } : {}),
          })}
        >
          Register
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

export function Modal({ label, onClose, children }: {
  label: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-label={label}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>{children}</div>
    </div>
  );
}
